export function extractApiError(error: unknown, fallback: string): string {
  const data =
    typeof error === 'object' && error !== null && 'response' in error
      ? (error as { response?: { data?: unknown } }).response?.data
      : undefined;
  if (typeof data === 'string' && data.trim()) return data;
  if (data && typeof data === 'object') {
    const d = data as Record<string, unknown>;
    if (typeof d.detail === 'string') return d.detail;
    const msgs = Object.entries(d)
      .flatMap(([k, v]) =>
        Array.isArray(v) ? v.map(s => `${k}: ${s}`) : typeof v === 'string' ? [`${k}: ${v}`] : [],
      )
      .join(' | ');
    if (msgs) return msgs;
  }
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const m = (error as { message?: unknown }).message;
    if (typeof m === 'string' && m.trim()) return m;
  }
  return fallback;
}

/**
 * Locale-aware error extraction.
 *
 * Mixed-locale regression (finding #20): a handful of BE serialiser
 * paths still emit Russian-only error strings (e.g. DRF's default
 * `Недопустимый первичный ключ "999" - объект не существует.`). When
 * the UI locale is English, surfacing that raw text reads like a bug.
 *
 * This helper extracts the raw message via extractApiError, then —
 * when the active locale is `en` and the extracted text contains
 * Cyrillic letters — returns `fallback` instead. The fallback is
 * expected to already be in English (callers compose it from i18n
 * keys), so the final UI is consistent.
 *
 * Russian locale: behaves exactly like extractApiError (i.e. surfaces
 * the BE detail as-is).
 */
export function extractApiErrorLocalised(
  error: unknown,
  fallback: string,
  locale: 'ru' | 'en',
): string {
  const raw = extractApiError(error, fallback);
  if (locale === 'ru') return raw;
  // English locale — drop Cyrillic-only BE strings on the floor. We
  // detect by looking for any cyrillic letter; mixed strings (e.g. a
  // pure English message that happens to embed a Russian zone name)
  // are still allowed through, only "the whole message is Russian"
  // gets replaced.
  if (/[Ѐ-ӿ]/.test(raw)) {
    return fallback;
  }
  return raw;
}
